import _ from 'underscore';
import OrderableItem from './hamlet-product-model-orderable-item';
import PricingBlock from './hamlet-product-model-pricing-block';


export default class HamletProductModel {
  productDescription: any;
  parentDescription: any;
  productKey?: string;
  userCountEnforced?: boolean;
  productContentData: any;
  contentData: any;
  orderableItems: any;
  monthly: any;
  annual: any;
  productType?: string;
  parentKey?: any[];
  $renewalItems: never[];
  $upgradeItems: never[];
  $licenseItems: any;

  constructor(options: HamletProductModel, productContentData: any) {
    var defaults = {
      productKey: null,            // jira
      productDescription: null,    // Jira
      productType: null,           // PERPETUAL, HOSTED, ADDON, HOSTED_ADDON, EVALUATION, SUBSCRIPTION
      parentKeys: null,
      parentDescription: null,
      userCountEnforced: false,
      marketplaceAddon: false,
      lastModified: (new Date()).toISOString(),
      contactSalesForAdditionalPricing: false,
      orderableItems: [],          // User-tier based pricing will have orderable items
      annual: [],                  // agent/role based pricing will have monthly/annual blocks defining the block limits
      monthly: [],
      billingType: null
    };

    Object.assign(this, defaults, options);
    this.productDescription = this.productDescription ? this.productDescription.replace(/\s*\((Server|Cloud)\)/, '') : null;
    this.parentDescription = this.parentDescription ? this.parentDescription.replace(/\s*\((Server|Cloud)\)/, '') : null;

    if (this.productKey === 'com.atlassian.servicedesk' || this.productKey === 'com.atlassian.servicedesk.ondemand') {
      this.userCountEnforced = false;
    }

    // Get Content Data
    this.productContentData = productContentData;
    if (this.productContentData.has(this.productKey)) {
      this.contentData = this.productContentData.get(this.productKey);
    } else {
      this.contentData = this.productContentData.createFromHamletApi(this);
    }

    if (this.contentData.overrides) {
      this.orderableItems = this.contentData.overrides.orderableItems ? this.contentData.orderableItems : this.orderableItems;
      this.monthly = this.contentData.overrides.monthly ? this.contentData.overrides.monthly : this.monthly;
      this.annual = this.contentData.overrides.annual ? this.contentData.overrides.annual : this.annual;
    }

    if (this.productType === 'ADDON' || this.productType === 'HOSTED_ADDON') {
      if (_.isEmpty(this.parentKeys)) {
        this.parentKeys = this.parentKey && [this.parentKey] || [];

        // Multiple parent compatibility hack (for data center)
        switch (this.parentKey) {

          case 'jira':
            this.parentKeys.push('jira-data-center');
            break;

          case 'confluence':
            this.parentKeys.push('confluence-data-center');
            break;

          case 'stash':
            this.parentKeys.push('stash-data-center');
            break;
        }
      }
    }

    var self = this;
    var orderables = _.uniq(this.orderableItems, 'orderableItemId');
    this.orderableItems = [];
    this.$renewalItems = [];
    this.$upgradeItems = [];
    _.each(orderables, function (item) {
      var orderable = new OrderableItem(item, self);
      if (orderable.saleType === 'RENEWAL') {
        self.$renewalItems.push(orderable);
      } else if (orderable.saleType === 'UPGRADE') {
        self.$upgradeItems.push(orderable);
      } else {
        self.orderableItems.push(orderable);
      }
    });
    this.orderableItems = this.orderableItems.sort(this.sortOrderables);
    this.$renewalItems = this.$renewalItems.sort(this.sortOrderables);
    this.$upgradeItems = this.$upgradeItems.sort(this.sortOrderables);
    this.setLicenseItems();

    _.each(['monthly', 'annual'], function (section) {
      var original = self[section];
      self[section] = [];
      _.each(original, function (block) {
        self[section].push(new PricingBlock(block, self));
      });
    });
  }

  sortOrderables(a, b) {
    var key = (a.$tierHierarchy === b.$tierHierarchy) ? 'amount' : '$tierHierarchy';
    return a[key] - b[key];
  }

  findOrderables(id) {
    var matches = [];
    matches = matches.concat(_.where(this.orderableItems, { orderableItemId: id }));
    matches = matches.concat(_.where(this.$renewalItems, { orderableItemId: id }));
    matches = matches.concat(_.where(this.$upgradeItems, { orderableItemId: id }));
    return matches;
  }

  hasAnyOrderables() {
    return this.orderableItems.length > 0;
  }

  hasOrderable(id) {
    return this.findOrderables(id).length > 0;
  }

  getOrderable(id) {
    return this.findOrderables(id)[0];
  }

  addOrderable(item) {
    if (!this.hasOrderable(item.orderableItemId)) {
      var orderable = new OrderableItem(item, this);
      if (orderable.saleType === 'RENEWAL') {
        this.$renewalItems.push(orderable);
      } else if (orderable.saleType === 'UPGRADE') {
        this.$upgradeItems.push(orderable);
      } else {
        this.orderableItems.push(orderable);
      }
      this.orderableItems = this.orderableItems.sort(this.sortOrderables);
      this.$renewalItems = this.$renewalItems.sort(this.sortOrderables);
      this.$upgradeItems = this.$upgradeItems.sort(this.sortOrderables);
      this.setLicenseItems();
    }
  }

  setLicenseItems() {
    var self = this,
      items = _.groupBy(this.orderableItems, 'licenseType');

    this.$licenseItems = this.$licenseItems || {};
    _.each(items, function (list, key) {
      self.$licenseItems[key] = list.sort(self.sortOrderables);
    });
  }

  isCompatibleWith(parent: any) {
    return _.contains(this.parentKeys, parent);
  }

  hasTheseLicenses(types: any) {

    var currentLicenses = _.uniq(_.pluck(this.orderableItems, 'licenseType')),
      licenseTypes = _.map(types, function (type) { return type.toUpperCase(); }),
      difference = _.difference(licenseTypes, currentLicenses);

    if (_.contains(['clover'], this.productKey)) {
      return _.has(this.$licenseItems, 'COMMERCIAL') && _.has(this.$licenseItems, 'ACADEMIC');
    } else if (/university/.test(this.productKey) || /training/.test(this.productKey)) {
      return _.has(this.$licenseItems, 'COMMERCIAL');
    } else {
      return difference.length === 0;
    }
  }
}
